import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import LoanCalculator from "@/components/LoanCalculator";
import { LOAN_PRODUCTS } from "@/lib/loanProducts";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Zap, HeartHandshake, TrendingUp, CheckCircle2 } from "lucide-react";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/9b28b915-9b41-4ce7-9d16-efe05e4d43e5/images/88f6a982e4235b4cb2d8b7f16551d28ff4a1ee4623fc64b028bb26e59489ab28.png";
const LOAN_ILLUS =
  "https://static.prod-images.emergentagent.com/jobs/9b28b915-9b41-4ce7-9d16-efe05e4d43e5/images/1a853eb8b62b17e030b5f37a7ae2b57c43f1d2087d107b255ee28f6d54c8d4c9.png";
const PROS = "https://images.unsplash.com/photo-1666886573440-16ac24f89e31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwcHJvZmVzc2lvbmFsc3xlbnwwfHx8fDE3ODAyMjgzMDB8MA&ixlib=rb-4.1.0&q=85";
const FARMER = "https://customer-assets.emergentagent.com/job_yambukai-finance/artifacts/0n3mwuir_Picture2.jpg";

const stats = [
  { value: "<24h", label: "Civil-servant decisioning" },
  { value: "6", label: "Loan products" },
  { value: "0%", label: "Hidden fees" },
  { value: "100%", label: "Personalised service" },
];

const valueProps = [
  { icon: Zap, title: "Fast decisions", text: "Salary-based and civil-servant applications decisioned within one business day." },
  { icon: ShieldCheck, title: "Responsible lending", text: "Transparent rates, no hidden fees, and repayments sized to your real cashflow." },
  { icon: HeartHandshake, title: "Personalised service", text: "Real people, real conversations. We work with you, not against you." },
  { icon: TrendingUp, title: "Built to grow with you", text: "From your first salary loan to financing your business — one partner, your whole journey." },
];

export default function Home() {
  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center opacity-90"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#F9F6F0]/95 via-[#F9F6F0]/85 to-[#F9F6F0]/70" />
        <div className="mx-auto max-w-7xl px-4 pb-20 pt-16 sm:px-6 lg:px-8 lg:pb-32 lg:pt-24">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#5B2C82]/20 bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#5B2C82] backdrop-blur" data-testid="hero-eyebrow">
                <span className="h-2 w-2 rounded-full bg-[#E87722]" />
                Microfinance for modern Zimbabwe
              </div>
              <h1 className="font-heading text-4xl font-bold leading-[1.05] text-ink sm:text-5xl lg:text-6xl">
                Capital that meets you{" "}
                <span className="relative inline-block">
                  <span className="relative z-10 text-[#5B2C82]">where you are</span>
                  <span className="absolute bottom-1 left-0 right-0 -z-0 h-3 bg-[#E87722]/40" />
                </span>
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-ink-soft sm:text-lg">
                From a civil servant bridging the month, to a farmer planning the next harvest,
                to a small business chasing its next contract — Joseph Roberts Capital builds the
                right loan, with the right terms, for the real Zimbabwe.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/apply">
                  <Button
                    size="lg"
                    className="w-full bg-[#E87722] px-7 py-6 text-base font-semibold text-white hover:bg-[#cc6517] sm:w-auto"
                    data-testid="hero-apply-btn"
                  >
                    Start your application
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/products">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full border-[#5B2C82] px-7 py-6 text-base font-semibold text-[#5B2C82] hover:bg-[#5B2C82] hover:text-white sm:w-auto"
                    data-testid="hero-products-btn"
                  >
                    Explore loan products
                  </Button>
                </Link>
              </div>

              <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                {stats.map((s) => (
                  <div key={s.label} className="border-l-2 border-[#E87722] pl-3">
                    <div className="font-heading text-2xl font-bold text-ink">{s.value}</div>
                    <div className="text-xs text-ink-muted">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <img
                  src={LOAN_ILLUS}
                  alt="Digital wallet"
                  className="relative z-10 mx-auto w-full max-w-md rounded-2xl"
                />
                <div className="absolute -bottom-4 -right-4 -z-0 h-32 w-32 rounded-full bg-[#E87722]/30 blur-2xl sm:h-48 sm:w-48" />
                <div className="absolute -left-4 -top-4 -z-0 h-32 w-32 rounded-full bg-[#5B2C82]/30 blur-2xl sm:h-48 sm:w-48" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="border-y border-[#E5E2DE] bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-4">
            {valueProps.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="group">
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#F9F6F0] text-[#5B2C82] transition-colors group-hover:bg-[#5B2C82] group-hover:text-white">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-ink">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-soft">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-2xl">
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#E87722]">Loan products</div>
              <h2 className="font-heading text-3xl font-semibold text-ink sm:text-4xl lg:text-5xl">
                One application. <span className="text-[#5B2C82]">Six tailored paths.</span>
              </h2>
              <p className="mt-4 text-base text-ink-soft">
                Our intuitive application detects who you are — a civil servant, salaried
                employee, farmer or business owner — and quietly routes you to the right
                product with the right rates.
              </p>
            </div>
            <Link to="/products" className="group inline-flex items-center gap-2 font-semibold text-[#5B2C82] hover:text-[#E87722]" data-testid="home-see-all-products">
              See all products <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {LOAN_PRODUCTS.map((p, i) => (
              <Link
                key={p.key}
                to="/apply"
                state={{ product: p.key }}
                data-testid={`product-card-${p.key}`}
                className={`group relative flex flex-col rounded-2xl border border-[#E5E2DE] bg-white p-6 transition-all hover:-translate-y-1 hover:border-[#5B2C82] hover:shadow-lg ${
                  i === 0 ? "md:col-span-2 lg:col-span-1" : ""
                }`}
              >
                <div className="mb-4 flex items-center justify-between">
                  <span className="rounded-full bg-[#F9F6F0] px-2.5 py-0.5 text-xs font-semibold text-[#5B2C82]">
                    {p.termRange}
                  </span>
                  <ArrowRight className="h-4 w-4 text-ink-muted transition-transform group-hover:translate-x-1 group-hover:text-[#5B2C82]" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-ink">{p.title}</h3>
                <p className="mt-1 text-sm font-medium text-[#E87722]">{p.tagline}</p>
                <p className="mt-3 text-sm leading-relaxed text-ink-soft">{p.description}</p>
                <div className="mt-5 flex items-center justify-between border-t border-[#E5E2DE] pt-4 text-xs text-ink-muted">
                  <span>${p.minAmount.toLocaleString()} – ${p.maxAmount.toLocaleString()}</span>
                  <span>{p.termRange}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CIVIL SERVANT SPOTLIGHT */}
      <section className="bg-[#5B2C82] py-20 text-white sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <img
                src={PROS}
                alt="Professionals"
                className="aspect-[4/5] w-full rounded-2xl object-cover"
              />
            </div>
            <div className="lg:col-span-7">
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#E87722]">For civil servants & salaried employees</div>
              <h2 className="font-heading text-3xl font-semibold sm:text-4xl lg:text-5xl">
                If you have a payslip, you have an <span className="text-[#E87722]">advantage.</span>
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/80">
                Government and salaried employees enjoy our lowest rates, lightest paperwork and
                fastest turnarounds. Repayments deduct quietly in the background — and your
                application stays under-24-hours fast.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Lowest rates: from 12% p.a.",
                  "Repayments by salary deduction or stop order",
                  "Up to $10,000 over 36 months",
                  "Decision within 24 hours, end to end",
                ].map((l) => (
                  <li key={l} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#E87722]" />
                    <span className="text-white/90">{l}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/apply">
                  <Button className="bg-[#E87722] px-7 py-6 text-base font-semibold text-white hover:bg-[#cc6517]" data-testid="civil-servant-cta">
                    Apply as civil servant <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 grid gap-6 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#E87722]">Plan ahead</div>
              <h2 className="font-heading text-3xl font-semibold text-ink sm:text-4xl lg:text-5xl">
                Try before you apply.
              </h2>
              <p className="mt-4 max-w-xl text-base text-ink-soft">
                Slide your way to a comfortable monthly payment. No commitment. No credit
                check. Just clarity.
              </p>
            </div>
          </div>
          <LoanCalculator />
        </div>
      </section>

      {/* AGRI BAND */}
      <section className="relative overflow-hidden bg-[#E87722] py-16 text-white sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:items-center lg:px-8">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-3xl font-semibold sm:text-4xl">From seed to harvest — we finance the journey.</h2>
            <p className="mt-4 max-w-xl text-white/90">
              Agricultural loans aligned to your cropping cycle. Repay when you harvest, not
              when the calendar says so.
            </p>
            <Link to="/apply" className="mt-6 inline-flex">
              <Button className="bg-white text-[#5B2C82] hover:bg-white/90" data-testid="agri-cta">
                Apply for an agricultural loan
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-5">
            <img src={FARMER} alt="Farmer" className="aspect-[4/3] w-full rounded-2xl object-cover" />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#E87722]">How it works</div>
            <h2 className="font-heading text-3xl font-semibold text-ink sm:text-4xl lg:text-5xl">
              From application to disbursement in <span className="text-[#5B2C82]">four clear steps.</span>
            </h2>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              { n: "01", t: "Tell us who you are", d: "Civil servant, salaried employee, farmer or business owner. Our wizard adapts." },
              { n: "02", t: "Apply online", d: "Provide ID, payslip or business details. Save and continue anytime." },
              { n: "03", t: "Get a decision", d: "We assess your risk and affordability — and tell you where you stand." },
              { n: "04", t: "Receive funds & repay", d: "Track everything — disbursement, schedule, payments — from your portal." },
            ].map((s) => (
              <div key={s.n} className="relative">
                <div className="font-heading text-5xl font-bold text-[#5B2C82]/15">{s.n}</div>
                <h3 className="mt-2 font-heading text-lg font-semibold text-ink">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-soft">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-[#E5E2DE] bg-white py-16">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-3xl font-semibold text-ink sm:text-4xl">
            Ready when you are.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-ink-soft">
            Whether it's school fees, a new asset for your business or a bridge until payday — your
            next chapter is a few minutes away.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/apply">
              <Button size="lg" className="w-full bg-[#5B2C82] px-7 py-6 text-base font-semibold text-white hover:bg-[#46225f] sm:w-auto" data-testid="final-apply-btn">
                Apply now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="w-full border-[#5B2C82] px-7 py-6 text-base font-semibold text-[#5B2C82] hover:bg-[#5B2C82] hover:text-white sm:w-auto" data-testid="final-contact-btn">
                Speak to a consultant
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
